/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { FC, memo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useDispatch } from "react-redux";

import { JSS_MEDIA_TABLET_MAX } from "~/constants";
import MenuContent from "~/features/header/components/menucontent/MenuContent";
import { HeaderActions } from "~/features/header/slice";
import useResizeObserver from "~/hooks/useResizeObserver";

const useStyles = createUseStyles({
  menuContent: {
    [JSS_MEDIA_TABLET_MAX]: {
      display: "none",
    },
  },
});

const DesktopMenu: FC = () => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const navRef = useRef<HTMLElement>(null);

  const onMenuResize = useCallback(
    (menu: ResizeObserverEntry) => {
      dispatch(HeaderActions.setMenuWidth(menu.contentRect.width));
    },

    [dispatch],
  );

  useResizeObserver(navRef, onMenuResize);

  useEffect(
    () => {
      if (navRef.current) {
        const { width } = navRef.current.getBoundingClientRect();
        dispatch(HeaderActions.setMenuWidth(width));
      }
    },
    // Observer doesn't detect changes sometimes when language changes, needs manual adjustments
    // Lazy loading the component requires initial load
    [dispatch, i18n.language],
  );

  return <MenuContent navRef={navRef} className={classes.menuContent} />;
};

export default memo(DesktopMenu);
